import {sumBy, get} from "lodash-es"
import moment from 'moment'
import {groupBy} from "lowscore";
import titlecase from 'title-case'
import roomTypes from "root-packages/accommodation/roomTypes.js";
import {calculateLegsForItinerary} from "../flights/tropicsLeg.js"

export default class BookingPriceBreakdown {
  constructor ({booking, config}) {
    this.booking = booking
    this.config = config
  }

  prepareExtraStays () {
    return this.getAdditionalProducts().reduce((accumulator, product) => {
      if (this.isExtraStayProduct(product)) {
        const key = product.type === 'accommodationPre' ? 'before' : 'after'
        const nights = product.stay[0].nights

        accumulator[key] = {
          nights: nights,
          name: product.name,
        }
      }

      return accumulator
    }, {})
  }

  preparePromisedFlights () {
    return {
      legs: get(this.booking, 'passengers[0].promisedFlights[0].itinerary', []),
    }
  }

  prepareFlights () {
    let legs = []
    if (this.booking.flights) {
      const flightItinerary = this.booking.flights[0].itinerary.map((leg) => {
        const isFlightBeforeTour = moment(leg.departDate).isBefore(moment(this.getTour().startDate.date).add(1, 'day'))

        return {
          ...leg,
          requestedSegmentRef: isFlightBeforeTour ? '0' : '1',
        }
      })

      legs = calculateLegsForItinerary(flightItinerary)
    }

    return {
      legs,
    }
  }

  preparePrices () {
    return [
      ...this.prepareRoomsPrices(),
      ...this.preparePromisedFlightsPrices(),
      ...this.prepareIncludedProductsPrices(),
      ...this.prepareAdditionalProductsPrices(),
      ...this.prepareDiscountsPrices(),
    ]
  }

  prepareRoomsPrices () {
    const groups = groupBy(this.getRooms(), (room) => room.roomType)
    const allPassengers = this.getPassengers()
    const promisedFlightsPricePerPassenger = this.getPromisedFlightsPrice() / allPassengers.length

    return Object.entries(groups).map(([groupName, rooms]) => {
      const count = rooms.length
      const name = titlecase(roomTypes.displayNameForName(groupName))
      const passengers = rooms.flatMap(room => room.passengers)
      const basePrice = sumBy(passengers, (passenger) => passenger.price.base)
      const includedProductsPrice = this.showMiscellaneousProductBreakdown() ? 0 : sumBy(passengers, (passenger) => sumBy(passenger.price.included, 'price'))
      const promisedFlightsPrice = this.arePromisedFlightsBundled() ? promisedFlightsPricePerPassenger * passengers.length : 0

      return {
        name: `${name} Room (${count})`,
        price: basePrice + includedProductsPrice + promisedFlightsPrice,
        isDiscount: false,
      }
    })
  }

  preparePromisedFlightsPrices () {
    const price = this.getPromisedFlightsPrice()
    const promisedFlights = []

    if (price > 0) {
      promisedFlights.push({
        name: 'International Flights',
        price: this.arePromisedFlightsBundled() ? 'Included' : price,
        isDiscount: false,
      })
    }

    return promisedFlights
  }

  prepareIncludedProductsPrices () {
    let products = []

    if (this.showMiscellaneousProductBreakdown()) {
      products = this.getIncludedProducts().map(product => {
        return {
          name: product.name,
          price: product.totalPrice,
          isDiscount: false,
        }
      })
    }

    return products
  }

  prepareAdditionalProductsPrices () {
    return this.getAdditionalProducts().reduce((accumulator, product) => {
      if (this.isExtraStayProduct(product)) {
        const type = product.type === 'accommodationPre' ? 'Pre-trip' : 'Post-trip'
        const nights = product.stay[0].nights

        accumulator.push({
          name: `${type} accommodation (${nights})`,
          price: product.totalPrice,
          isDiscount: false,
        })
      } else if (this.isFlightProduct(product)) {
        const prices = []

        prices.push({
          name: 'Flights',
          price: product.totalPrice,
          isDiscount: false,
        })

        if (Array.isArray(product.promotions) && product.promotions.length > 0) {
          prices.push(
            ...product.promotions.map((promo) => ({
              name: promo.name,
              price: promo.totalAmount,
              isDiscount: true,
            }))
          )
        }
        accumulator.push(...prices)
      } else {
        accumulator.push({
          name: product.name,
          price: product.totalPrice,
          isDiscount: false,
        })
      }

      return accumulator
    }, [])
  }

  prepareDiscountsPrices () {
    return this.getDiscounts().map(product => {
      return {
        name: product.name,
        price: product.totalAmount,
        isDiscount: true,
      }
    })
  }

  getTour () {
    return this.booking.tours[0]
  }

  getRooms () {
    const tour = this.getTour()
    return Array.isArray(tour.rooms) ? tour.rooms : []
  }

  getPassengers () {
    return Array.isArray(this.booking.passengers) ? this.booking.passengers : []
  }

  getAdditionalProducts () {
    const tour = this.getTour()
    return Array.isArray(tour.additionalProducts) ? tour.additionalProducts : []
  }

  getIncludedProducts () {
    const tour = this.getTour()
    return Array.isArray(tour.included) ? tour.included : []
  }

  getDiscounts () {
    const tour = this.getTour()
    return Array.isArray(tour.discounts) ? tour.discounts : []
  }

  isExtraStayProduct (product) {
    return ['accommodationPost', 'accommodationPre'].includes(product.type)
  }

  isFlightProduct (product) {
    return product.type === 'AIR'
  }

  getPromisedFlightsPrice () {
    return sumBy(this.getPassengers(), (passenger) => {
      if (passenger.promisedFlights) {
        return sumBy(passenger.promisedFlights, 'price')
      }
      return 0
    })
  }

  arePromisedFlightsBundled () {
    return this.config.promisedFlights.pricing === 'bundled'
  }

  showMiscellaneousProductBreakdown () {
    return this.config.features.taMiscellaneousProductBreakdown
  }

  prepareKnoCommerceProductsList () {
    const includedProducts = this.getIncludedProducts().map(product => ({
      name: product.name,
      price: product.totalPrice,
      product_id: product.id,
    }))

    const additionalProducts = this.getAdditionalProducts().map(product => ({
      name: product.name,
      price: product.totalPrice,
      product_id: product.id,
    }))

    return [
      ...includedProducts,
      ...additionalProducts,
    ]
  }

  getBookingPriceBreakdown () {
    return {
      totalPrice: this.booking.totalPrice,
      extraStays: this.prepareExtraStays(),
      promisedFlights: this.preparePromisedFlights(),
      flights: this.prepareFlights(),
      prices: this.preparePrices(),
      knoCommerceProductsList: this.prepareKnoCommerceProductsList(),
    }
  }
}
